import { z } from "zod";

export const DailySummaryTaskSchema = z.object({ id: z.uuid(), title: z.string() }).strict();
const GroupSchema = z
  .object({ count: z.number().int().nonnegative(), tasks: z.array(DailySummaryTaskSchema).max(20) })
  .strict();
export const DailySummarySchema = z
  .object({
    workspace_id: z.uuid(),
    local_date: z.iso.date(),
    timezone: z.string(),
    generated_at: z.iso.datetime(),
    children: z.array(
      z
        .object({
          id: z.uuid(),
          name: z.string(),
          completed: GroupSchema,
          remaining: GroupSchema,
          carryover: GroupSchema,
          help: GroupSchema,
          awaiting_review: GroupSchema,
          deferred: z.number().int().nonnegative(),
          skipped: z.number().int().nonnegative(),
          cancelled: z.number().int().nonnegative(),
        })
        .strict(),
    ),
  })
  .strict();
export const DailySummarySettingsInputSchema = z
  .object({
    expected_version: z.number().int().nonnegative(),
    enabled: z.boolean(),
    web_push: z.boolean(),
    email: z.boolean(),
  })
  .strict();
export const DailySummaryEmailRequestSchema = z
  .object({ expected_version: z.number().int().nonnegative(), email: z.email().max(254) })
  .strict();
export const DailySummaryEmailConfirmSchema = z
  .object({ challenge_id: z.uuid(), code: z.string().regex(/^\d{6}$/u) })
  .strict();
export const DailySummaryDeliveryStatusSchema = z.enum([
  "pending",
  "sending",
  "accepted",
  "failed",
  "unknown",
  "cancelled",
]);
export const DailySummarySettingsResponseSchema = z
  .object({
    settings: z
      .object({
        version: z.number().int().nonnegative(),
        enabled: z.boolean(),
        web_push: z.boolean(),
        email: z.boolean(),
        email_address: z.email().nullable(),
        email_verified: z.boolean(),
        timezone: z.string(),
        send_time: z.literal("20:00"),
        email_available: z.boolean(),
        push_devices: z.number().int().nonnegative(),
      })
      .strict(),
    verification: z
      .object({
        id: z.uuid(),
        email: z.email(),
        expires_at: z.iso.datetime(),
        status: DailySummaryDeliveryStatusSchema,
      })
      .strict()
      .nullable(),
    deliveries: z.array(
      z
        .object({
          id: z.uuid(),
          local_date: z.iso.date(),
          channel: z.enum(["web_push", "email"]),
          status: DailySummaryDeliveryStatusSchema,
          updated_at: z.iso.datetime(),
          error_code: z.string().nullable(),
        })
        .strict(),
    ),
    request_id: z.string(),
  })
  .strict();
export const DailySummaryResponseSchema = z
  .object({ summary: DailySummarySchema, request_id: z.string() })
  .strict();
export const DailySummaryDeliveryJobSchema = z
  .object({
    schema_version: z.literal(1),
    type: z.literal("notification.daily_summary.deliver"),
    workspace_id: z.uuid(),
    delivery_id: z.uuid(),
  })
  .strict();
export type DailySummary = z.infer<typeof DailySummarySchema>;
export type DailySummarySettingsInput = z.infer<typeof DailySummarySettingsInputSchema>;
export type DailySummaryDeliveryJob = z.infer<typeof DailySummaryDeliveryJobSchema>;
